import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, Phone, Filter, CheckCircle2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  neu: "Neu",
  kontaktiert: "Kontaktiert",
  termin_gebucht: "Termin gebucht",
  angebot_erstellt: "Angebot erstellt",
  rechnung_gestellt: "Rechnung gestellt",
  bezahlt: "Bezahlt",
  aufgenommen: "Aufgenommen",
  abgeschlossen: "Abgeschlossen",
};

const STATUS_ORDER = Object.keys(STATUS_LABELS);
const STATUS_COLORS = {
  neu: "bg-blue-500/15 text-blue-200 border-blue-400/30",
  kontaktiert: "bg-purple-500/15 text-purple-200 border-purple-400/30",
  termin_gebucht: "bg-teal-500/15 text-teal-200 border-teal-400/30",
  angebot_erstellt: "bg-amber-500/15 text-amber-200 border-amber-400/30",
  rechnung_gestellt: "bg-orange-500/15 text-orange-200 border-orange-400/30",
  bezahlt: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  aufgenommen: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30",
  abgeschlossen: "bg-white/10 text-white/60 border-white/20",
};

const INTERESSE_LABEL = {
  master: "SAT MASTER",
  mentor: "SAT MENTOR",
  herzbewegung: "Herzbewegung",
  orientierung: "Orientierung",
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("sat_token") || ""}`,
});

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await fetch(`${API}/api/admin/leads/erstgespraech`, { headers: authHeaders() });
      if (!r.ok) throw new Error("Zugriff verweigert");
      const data = await r.json();
      setLeads(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      const r = await fetch(`${API}/api/admin/leads/erstgespraech/${id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error("Update fehlgeschlagen");
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const counts = STATUS_ORDER.reduce((a, s) => { a[s] = leads.filter((l) => l.status === s).length; return a; }, {});

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#050B1B] text-white" data-testid="admin-leads-page">
      <div className="container-sat max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline">SAT Admin</div>
            <h1 className="serif text-4xl md:text-5xl font-light mt-2">Erstgespräche</h1>
            <p className="text-white/55 text-sm mt-2">{leads.length} Anfragen · {counts.neu || 0} neu · {counts.termin_gebucht || 0} Termine gebucht</p>
          </div>
          <Link to="/admin" className="text-white/50 hover:text-white text-sm">← Admin-Dashboard</Link>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilter("all")}
            className={`text-xs px-3 py-2 rounded-full border ${filter === "all" ? "bg-white/20 border-white/40" : "border-white/10 hover:bg-white/[0.04]"}`}
            data-testid="admin-leads-filter-all"
          >
            <Filter size={12} className="inline mr-1" /> Alle ({leads.length})
          </button>
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-2 rounded-full border ${filter === s ? "bg-white/20 border-white/40" : "border-white/10 hover:bg-white/[0.04]"}`}
              data-testid={`admin-leads-filter-${s}`}
            >
              {STATUS_LABELS[s]} ({counts[s] || 0})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
        ) : err ? (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-red-200"><AlertCircle size={16} className="inline mr-2" />{err}</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-16 text-center text-white/50 italic">
            Noch keine Anfragen in diesem Status.
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((l) => (
              <div key={l.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid={`admin-lead-${l.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${STATUS_COLORS[l.status]}`}>
                        {STATUS_LABELS[l.status] || l.status}
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]/80">{INTERESSE_LABEL[l.interesse] || l.interesse}</span>
                    </div>
                    <div className="serif text-2xl font-light">{l.name}</div>
                    <div className="text-white/50 text-sm mt-1 flex flex-wrap gap-4">
                      <a href={`mailto:${l.email}`} className="hover:text-white flex items-center gap-1"><Mail size={12} /> {l.email}</a>
                      {l.telefon && <a href={`tel:${l.telefon}`} className="hover:text-white flex items-center gap-1"><Phone size={12} /> {l.telefon}</a>}
                    </div>
                    {l.nachricht && (
                      <p className="text-white/70 text-sm mt-3 italic max-w-2xl leading-relaxed border-l-2 border-[#D4AF37]/40 pl-3">
                        {l.nachricht}
                      </p>
                    )}
                    {l.wunschtermin && <p className="text-white/40 text-xs mt-2">Wunschtermin: {l.wunschtermin}</p>}
                    <p className="text-white/30 text-[11px] mt-3">
                      {new Date(l.created_at).toLocaleString("de-CH")}
                      {l.mail_status?.user && ` · Mail user: ${l.mail_status.user}`}
                      {l.newsletter_opt_in && " · Newsletter ✓"}
                    </p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Status ändern</div>
                    <select
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className="bg-[#081328] border border-white/15 rounded-lg px-3 py-2 text-sm text-white"
                      data-testid={`admin-lead-status-${l.id}`}
                    >
                      {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-14 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.05] p-5 text-sm text-emerald-100 flex items-start gap-3">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold mb-1">Rechnungs-Flow (MVL)</div>
            Wähle bei Interessent „Angebot erstellt&ldquo; oder „Rechnung gestellt&ldquo;, sobald du das Angebot manuell verschickt hast.
            Die eigentliche Rechnung wird ausserhalb der Plattform erstellt (Bexio, DeinRechnungsprogramm, etc.).
          </div>
        </div>
      </div>
    </div>
  );
}
